/**
 * Paginação (§6.1).
 *
 * ── Duas formas, e o motivo de não ser uma só ─────────────────────────────
 *
 * **Cursor** para mensagem e timeline: o conteúdo cresce pela ponta, e offset
 * produz duplicata ou buraco assim que chega item novo entre duas páginas. Com
 * cinquenta mensagens por conversa isso é raro; com uma conversa ativa é o
 * comportamento normal.
 *
 * **Estável** para lista operacional: ordenação determinística com desempate
 * por ID, e a página N é a mesma enquanto nada mudar. Cursor ali atrapalharia —
 * quem opera precisa poder ir para a página 4 e voltar.
 *
 * ── Por que o cursor é opaco ──────────────────────────────────────────────
 *
 * Cursor legível vira campo que o cliente tenta construir — e no dia em que a
 * ordenação mudar, o cliente quebra e a culpa parece nossa. Opaco e assinado,
 * a única origem possível é uma resposta nossa.
 *
 * A assinatura carrega o **tenant**: cursor de uma revenda apresentado por
 * outra é recusado antes de virar consulta. Sem isso, o cursor seria um
 * parâmetro vindo do cliente que atravessa direto para o `WHERE` — que é
 * exatamente o que a §13.1 proíbe.
 */

import { z } from 'zod';

/**
 * Página por cursor. `proximo` nulo é fim — e é diferente de ausente, que seria
 * "não sei". Cliente que trata os dois igual acaba num laço infinito.
 */
export function paginaPorCursor<T extends z.ZodType>(item: T) {
  return z.object({
    itens: z.array(item),
    proximo: z.string().nullable().describe('Cursor da próxima página. `null` é fim.'),
  });
}

/** Página estável, com total. Lista operacional precisa saber quantos são. */
export function paginaEstavel<T extends z.ZodType>(item: T) {
  return z.object({
    itens: z.array(item),
    pagina: z.number().int().min(1),
    tamanho: z.number().int().min(1),
    total: z.number().int().min(0),
  });
}

/** Teto do `tamanho` em qualquer listagem. */
export const TAMANHO_MAXIMO_DE_PAGINA = 100;
export const TAMANHO_PADRAO_DE_PAGINA = 25;

export const ParametrosDeCursorSchema = z.object({
  cursor: z.string().optional(),
  tamanho: z.coerce
    .number()
    .int()
    .min(1)
    .max(TAMANHO_MAXIMO_DE_PAGINA)
    .default(TAMANHO_PADRAO_DE_PAGINA),
});

export const ParametrosDePaginaSchema = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  tamanho: z.coerce
    .number()
    .int()
    .min(1)
    .max(TAMANHO_MAXIMO_DE_PAGINA)
    .default(TAMANHO_PADRAO_DE_PAGINA),
});

export type ParametrosDeCursor = z.infer<typeof ParametrosDeCursorSchema>;
export type ParametrosDePagina = z.infer<typeof ParametrosDePaginaSchema>;
